import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUp } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const BackToTop = () => {
  const { isDark } = useTheme();
  const [isVisible, setIsVisible] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  const handleScroll = useCallback(() => {
    // Try multiple scroll sources for compatibility
    const scrollTop = window.pageYOffset || 
                      document.documentElement.scrollTop || 
                      document.body.scrollTop || 
                      0;
    
    const docHeight = Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.offsetHeight,
      document.body.clientHeight,
      document.documentElement.clientHeight
    ) - window.innerHeight;
    
    const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
    
    setScrollProgress(Math.min(progress, 100));
    // Show button after scrolling 200px (reduced threshold for better UX)
    setIsVisible(scrollTop > 200);
  }, []);

  useEffect(() => {
    // Initial check
    handleScroll();
    
    // Add listeners to multiple targets for better compatibility
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('scroll', handleScroll, { passive: true });
    
    // Periodic check as fallback (some pages use custom scroll containers)
    const intervalId = setInterval(handleScroll, 500);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('scroll', handleScroll);
      clearInterval(intervalId);
    };
  }, [handleScroll]);

  const scrollToTop = () => {
    // Multiple methods for maximum browser compatibility
    try {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    } catch (e) {
      // Fallback for browsers that don't support smooth scroll
      window.scrollTo(0, 0);
    }
    
    // Additional fallbacks
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Also try scrollIntoView on the body
    document.body.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 20 }}
          onClick={scrollToTop}
          className={`fixed bottom-6 left-6 z-[9999] w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer ${
            isDark 
              ? 'bg-[#1C1917] text-white hover:bg-[#292524] border border-white/10' 
              : 'bg-white text-[#1C1917] hover:bg-gray-50 border border-black/10 shadow-lg'
          }`}
          style={{
            boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.1)'
          }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          data-testid="back-to-top"
          aria-label="Retour en haut"
        >
          {/* Progress ring */}
          <svg className="absolute inset-0 w-full h-full -rotate-90">
            <circle
              cx="24"
              cy="24"
              r="20"
              fill="none"
              stroke={isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}
              strokeWidth="2"
            />
            <circle
              cx="24"
              cy="24"
              r="20"
              fill="none"
              stroke={isDark ? '#E5A700' : '#5B2D8E'}
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 20}`}
              strokeDashoffset={`${2 * Math.PI * 20 * (1 - scrollProgress / 100)}`}
              style={{ transition: 'stroke-dashoffset 0.1s ease-out' }}
            />
          </svg>
          <ArrowUp className="w-5 h-5 relative z-10" />
        </motion.button>
      )}
    </AnimatePresence>
  );
};

export default BackToTop;
